class SkuPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="text"]');
    this.container = this.querySelector('.sku-search-container');
    
    this.debounceTimer = null;
    this.isLoading = false;
    this.currentSearchTerm = '';
    this.isProgrammaticUpdate = false; // Flag to prevent search when programmatically setting value
    
    if (this.input && this.container) {
      this.init();
    }
  }

  init() {
    // Create icon container (shows magnifying glass initially, spinner when loading)
    this.iconContainer = document.createElement('div');
    this.iconContainer.className = 'sku-search-icon-container';
    
    // Magnifying glass icon (default state) - using same icon font as search-bar
    this.searchIcon = document.createElement('span');
    this.searchIcon.className = 'icon icon-search';
    this.searchIcon.setAttribute('aria-hidden', 'true');
    
    // Spinner icon (shown when loading)
    this.spinner = document.createElement('div');
    this.spinner.className = 'sku-search-spinner';
    this.spinner.style.display = 'none';
    this.spinner.innerHTML = `
      <svg class="sku-search-spinner-svg" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="sku-search-spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="sku-search-spinner-path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    `;
    
    this.iconContainer.appendChild(this.searchIcon);
    this.iconContainer.appendChild(this.spinner);
    this.container.appendChild(this.iconContainer);

    // Create dropdown
    this.dropdown = document.createElement('div');
    this.dropdown.className = 'sku-search-dropdown';
    this.dropdown.style.display = 'none';
    this.resultsList = document.createElement('ul');
    this.resultsList.className = 'sku-search-results';
    this.dropdown.appendChild(this.resultsList);
    this.container.appendChild(this.dropdown);

    // Bind events
    this.input.addEventListener('input', this.debounce(this.handleInput.bind(this), 300));
    this.input.addEventListener('focus', this.handleFocus.bind(this));
    this.input.addEventListener('blur', this.handleBlur.bind(this));
    
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.hideDropdown();
      }
    });
  }

  debounce(fn, wait) {
    return (...args) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  handleInput(event) {
    // Skip search if this is a programmatic update
    if (this.isProgrammaticUpdate) {
      this.isProgrammaticUpdate = false;
      return;
    }

    const searchTerm = event.target.value.trim();
    this.currentSearchTerm = searchTerm;

    if (!searchTerm.length) {
      this.hideDropdown();
      this.hideSpinner();
      return;
    }

    this.showSpinner();
    this.searchSkus(searchTerm);
  }

  handleFocus(event) {
    const searchTerm = event.target.value.trim();
    if (searchTerm.length && this.resultsList.children.length > 0) {
      this.showDropdown();
    }
  }

  handleBlur(event) {
    // Delay to allow click events on dropdown items to fire
    setTimeout(() => {
      if (!this.container.contains(document.activeElement)) {
        this.hideDropdown();
      }
    }, 200);
  }

  async searchSkus(searchTerm) {
    if (!window.Shopify || !window.Shopify.routes) {
      console.error('Shopify routes not available');
      this.hideSpinner();
      return;
    }

    const apiUrl = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[options][fields]=variants.sku,variants.barcode&resources[limit]=10&section_id=sku-predictive-search`;

    try {
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      this.displayResultsFromHTML(text);
    } catch (error) {
      console.error('SKU search error:', error);
      this.showError();
    } finally {
      this.hideSpinner();
    }
  }

  displayResultsFromHTML(htmlText) {
    // Clear previous results
    this.resultsList.innerHTML = '';

    try {
      // Parse the HTML response
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');
      const sectionElement = doc.querySelector('#shopify-section-sku-predictive-search');
      
      if (!sectionElement) {
        this.showNoResults();
        return;
      }

      // Get settings from data attributes
      const noResultsText = sectionElement.getAttribute('data-no-results') || 'No SKUs found';

      // Get all result items from the section
      const resultItems = sectionElement.querySelectorAll('.sku-search-result-item');
      
      if (resultItems.length === 0) {
        this.showNoResults(noResultsText);
        return;
      }

      // Clone and append each result item
      resultItems.forEach((item) => {
        const clonedItem = item.cloneNode(true);
        const sku = clonedItem.getAttribute('data-sku') || clonedItem.querySelector('.sku-search-sku')?.textContent?.trim();
        
        if (sku) {
          // Highlight search term only in the SKU element, not the title
          if (this.currentSearchTerm) {
            const skuElement = clonedItem.querySelector('.sku-search-sku');
            if (skuElement) {
              this.highlightText(skuElement, this.currentSearchTerm);
            }
          }
          
          // Prevent blur from closing dropdown when clicking on link
          const link = clonedItem.querySelector('.sku-search-result-link');
          if (link) {
            link.addEventListener('mousedown', (e) => {
              e.stopPropagation();
            });
          }
          
          this.resultsList.appendChild(clonedItem);
        }
      });

      this.showDropdown();
    } catch (error) {
      console.error('Error parsing SKU search results:', error);
      this.showError();
    }
  }

  showNoResults(customText) {
    this.resultsList.innerHTML = '';
    const li = document.createElement('li');
    li.className = 'sku-search-no-results';
    li.textContent = customText || 'No SKUs found';
    this.resultsList.appendChild(li);
    this.showDropdown();
  }

  showError() {
    this.resultsList.innerHTML = '';
    const li = document.createElement('li');
    li.className = 'sku-search-error';
    li.textContent = 'Error searching SKUs. Please try again.';
    this.resultsList.appendChild(li);
    this.showDropdown();
  }

  selectSku(sku) {
    // Set flag to prevent search from triggering
    this.isProgrammaticUpdate = true;
    this.input.value = sku;
    // Trigger input event for any listeners (but our handler will skip the search)
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    // Trigger change event as well
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.hideDropdown();
    // Blur the input to prevent dropdown from reopening
    this.input.blur();
  }

  showSpinner() {
    if (this.searchIcon && this.spinner) {
      this.searchIcon.style.display = 'none';
      this.spinner.style.display = 'block';
      this.isLoading = true;
    }
  }

  hideSpinner() {
    if (this.searchIcon && this.spinner) {
      this.searchIcon.style.display = 'block';
      this.spinner.style.display = 'none';
      this.isLoading = false;
    }
  }

  showDropdown() {
    if (this.dropdown) {
      this.dropdown.style.display = 'block';
    }
  }

  hideDropdown() {
    if (this.dropdown) {
      this.dropdown.style.display = 'none';
    }
  }

  highlightText(element, searchTerm) {
    if (!searchTerm || !element) return;

    // Escape special regex characters in search term
    const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');

    // Walk through all text nodes in the element
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      // Skip if parent is already a mark element to avoid nested marks
      if (node.parentElement && node.parentElement.tagName === 'MARK') {
        continue;
      }
      textNodes.push(node);
    }

    textNodes.forEach(textNode => {
      const text = textNode.textContent;
      const matches = text.match(regex);
      
      if (matches && matches.length > 0) {
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        
        // Find all matches and create mark elements
        let match;
        const globalRegex = new RegExp(escapedTerm, 'gi');
        while ((match = globalRegex.exec(text)) !== null) {
          // Add text before match
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
          }
          
          // Add highlighted match
          const mark = document.createElement('mark');
          mark.textContent = match[0];
          fragment.appendChild(mark);
          
          lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text after last match
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        }
        
        // Replace the text node with the fragment
        textNode.parentNode.replaceChild(fragment, textNode);
      }
    });
  }
}

customElements.define('sku-predictive-search', SkuPredictiveSearch);

